import { OptionType } from '@insurancemenu/imenu-components-lib/dist/types';
import { RateSegment } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { getEffectiveDatesOptions } from 'global/services-helpers/domainService';
import { makeAutoObservable, reaction } from 'mobx';
import { getRootStoreInstance, RootStore } from 'store/rootStore';
import {
  DomainProductLineItem,
  DomainProductLinesDelegate,
} from 'store/modules/initiate-quote/domainProductLinesDelegate';
import {
  sunLifeGroupSizeValidation,
  sunLifeSICAdditionalValidations,
  sunLifeSICWarningMessage,
} from 'global/utils/sponsor-validations/sunLife/sicValidation';
import {
  INCORPORATION_DATE_VALIDATION_MSG,
  isIncorporationDateValid,
} from 'global/utils/sponsor-validations/sunLife/incorporationDateValidation';
import { ReactNode } from 'react';

interface DomainParams {
  rateSegment: RateSegment;
}

type SunLifeInitiateProposalFields = {
  proposalName: FormControl<string>;
  effectiveDate: FormControl<string>;
  incorporationDate: FormControl<string>;
  groupSize: FormControl<string>;
  zip: FormControl<string>;
  state: FormControl<string>;
  county: FormControl<string>;
  sic4: FormControl<string>;
};

export class SunLifeInitiateProposalFormDelegate {
  private readonly _rootStore: RootStore = getRootStoreInstance();
  public readonly domainProductLinesDelegate: DomainProductLinesDelegate;
  public proposalForm: FormController<SunLifeInitiateProposalFields>;

  public get domainProductLineItems(): DomainProductLineItem[] {
    return this.domainProductLinesDelegate.domainProductLineItems;
  }

  public get isDomainProductLinesLoading(): boolean {
    return this.domainProductLinesDelegate.isLoading;
  }

  public get effectiveDatesOptions(): OptionType[] {
    return getEffectiveDatesOptions(this._rootStore.domainStoreV2.effectiveDates);
  }

  private get _canFetchProductLines(): boolean {
    const {
      fields: { effectiveDate, zip, state, county, sic4 },
    } = this.proposalForm;

    return !!(
      !effectiveDate.unpassedValidationMsg &&
      !zip.unpassedValidationMsg &&
      !state.unpassedValidationMsg &&
      !county.unpassedValidationMsg &&
      !sic4.unpassedValidationMsg
    );
  }

  /** Shown as a warning `Alert` when the current SIC matches a non-blocking rule. */
  public get sicWarningMessage(): ReactNode | null {
    const sicValue = this.proposalForm.fields.sic4.value;
    return sicValue && sicValue.length === 4 ? sunLifeSICWarningMessage(sicValue) : null;
  }

  constructor(params: DomainParams) {
    this.proposalForm = FormController.create(
      {
        proposalName: new FormControl<string>('', {
          validations: ['printableCharacters'],
        }),
        effectiveDate: new FormControl<string>(''),
        groupSize: new FormControl<string>('', {
          customValidations: [sunLifeGroupSizeValidation],
        }),
        incorporationDate: new FormControl<string>('', {
          optional: true,
          enabledWithFields: ['effectiveDate'],
          customValidations: [
            {
              check: (incorporationDate: string): boolean => {
                return isIncorporationDateValid(incorporationDate, this.proposalForm.fields.effectiveDate.value);
              },
              msg: INCORPORATION_DATE_VALIDATION_MSG,
            },
          ],
        }),
      },
      {
        capabilities: [
          {
            type: 'zip',
            params: {
              zip: { optional: false },
              state: {
                customValidations: [
                  {
                    check: this._rootStore.domainStoreV2.isEligibleState,
                    msg: 'State is ineligible',
                  },
                ],
              },
            },
          },
          {
            type: 'sic',
            params: {
              additionalValidations: sunLifeSICAdditionalValidations,
            },
          },
        ],
      },
    );

    this.domainProductLinesDelegate = new DomainProductLinesDelegate({
      rateSegment: params.rateSegment,
    });

    reaction(
      () => [
        this.proposalForm.fields.effectiveDate.value,
        this.proposalForm.fields.zip.value,
        this.proposalForm.fields.state.value,
        this.proposalForm.fields.county.value,
        this.proposalForm.fields.sic4.value,
      ],
      async () => {
        if (!this._canFetchProductLines) {
          this.domainProductLinesDelegate.clearProductLines();
          return;
        }

        await this.domainProductLinesDelegate.getDomainProductLines(this.proposalForm.getFormDataObject(), true);
      },
    );

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
